var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import Vue from 'vue';
import Component from 'vue-class-component';
import { formOverridePropsProvider, formOverridePropsConsumer } from '../../plugins/form/formOverrideProps';
import { debounce, omit } from "lodash-es";
import { INJECT_UICORE_BUS, INJECT_UICORE_FORM, INJECT_UICORE_RENDERER } from "../../../../lib/utils/consts";
import { ObservableSet } from "../../../../lib/utils/collections";
import { normalizeFormValidateResult } from './FormValidateResult';
import { arrayify } from "../../../../lib/utils/array";
import { isMobile } from "../../../../lib/utils/browser/isMobile";
const FormContainerBaseBase = Vue.extend({
  props: {
    readonly: {
      type: [Boolean, String],
      default: false
    },
    required: {
      type: [Boolean, String],
      default: false
    },
    hasSuccessFeedback: {
      type: [Boolean, String],
      default: false
    },
    disabled: {
      type: [Boolean, String],
      default: false
    },
    layout: {
      type: String,
      default: ''
    },
    labelVertical: {
      type: String,
      default: ''
    },
    fillLine: {
      type: Boolean,
      default: false
    },
    labelWidth: {
      type: String,
      default: ''
    },
    titleMode: {
      type: String,
      default: 'pre'
    },
    mobileMode: {
      type: String,
      default: ''
    },
    isRoot: {
      type: Boolean,
      default: false
    } // internal! 不对外暴露
  },
  mixins: [formOverridePropsProvider, formOverridePropsConsumer],
  provide() {
    return {
      [INJECT_UICORE_FORM]: this
    };
  },
  inject: {
    parentForm: {
      from: INJECT_UICORE_FORM,
      default: null
    },
    ucRenderer: {
      from: INJECT_UICORE_RENDERER,
      default: null
    },
    uc: {
      from: INJECT_UICORE_BUS,
      default: null
    }
  },
  beforeCreate() {
    var _this$$options$propsD;
    // 如果是 rootForm 就不需要 inject parentForm 了
    if (this.$options.inject && (_this$$options$propsD = this.$options.propsData) !== null && _this$$options$propsD !== void 0 && _this$$options$propsD.isRoot) {
      this.$options.inject = omit(this.$options.inject, 'parentForm');
    }
  }
});
const validatingForms = new Set();
/**
 * 无论是表单，还是表单项之类的组件，他们都需要有一些通用的逻辑、computed 和 prop。
 *
 * 这里提供了那些通用的东西，包括：
 *
 * - 自动 (un)register self from parentForm
 * - 计算 finalLayout 等布局用的信息
 * - 提供遍历组件列表的方法
 * - and more
 */
let FormContainerBase = class FormContainerBase extends FormContainerBaseBase {
  constructor() {
    super(...arguments);
    /** 当前表单/表单项容器的 **直接** 子 subForms */
    this.subForms = new ObservableSet();
    /** 当前表单/表单项容器的 **直接** 子 editors，不包括嵌套的 */
    this.editors = new ObservableSet();
    /** 当前表单/表单项容器的 ID，只读！ */
    this.formComponentId = '';
  }
  /** 当前表单/表单项容器的 **直接** 子 editors 抛出的错误信息 */
  get validateResults() {
    const answer = [];
    this.editors.forEach(editor => {
      if (editor.validateResults.length) answer.push(...editor.validateResults);
    });
    return answer;
  }
  /** 根据继承关系计算出来的，当前表单/表单项容器的 layout 方式 */
  get finalLayout() {
    var _this$parentForm;
    if (isMobile() && !this.mobileMode) return 'vertical';
    return this.layout || ((_this$parentForm = this.parentForm) === null || _this$parentForm === void 0 ? void 0 : _this$parentForm.finalLayout) || 'horizontal_left';
  }
  get finalLabelVertical() {
    var _this$parentForm2;
    return this.labelVertical || ((_this$parentForm2 = this.parentForm) === null || _this$parentForm2 === void 0 ? void 0 : _this$parentForm2.finalLabelVertical) || 'default';
  }
  /** 根据继承关系计算出来的，当前表单/表单项的标签宽度 */
  get finalLabelWidth() {
    var _this$parentForm3;
    return this.labelWidth || ((_this$parentForm3 = this.parentForm) === null || _this$parentForm3 === void 0 ? void 0 : _this$parentForm3.finalLabelWidth) || '';
  }
  /** 根据继承关系计算出来的，当前表单/表单项的 fillLine */
  get finalFillLine() {
    var _this$parentForm4;
    return this.fillLine || ((_this$parentForm4 = this.parentForm) === null || _this$parentForm4 === void 0 ? void 0 : _this$parentForm4.finalFillLine) || false;
  }
  get finalTitleMode() {
    var _this$parentForm5;
    return this.titleMode || ((_this$parentForm5 = this.parentForm) === null || _this$parentForm5 === void 0 ? void 0 : _this$parentForm5.finalTitleMode) || 'pre';
  }
  // -------------------------------------------
  forEachSubForms(callback) {
    this.subForms.forEach(subForm => {
      callback(subForm);
      subForm.forEachSubForms(callback);
    });
  }
  forEachEditors(callback) {
    this.editors.forEach(callback);
    this.subForms.forEach(subForm => {
      subForm.forEachEditors(callback);
    });
  }
  /**
   * 对整个表单进行重置，将所有字段值重置为初始值并移除校验结果
   */
  async reset() {
    const promises = [];
    this.clearValidate();
    this.forEachEditors(editor => {
      if (typeof (editor === null || editor === void 0 ? void 0 : editor.reset) !== 'function') return;
      const promise = (async () => editor.reset())();
      promises.push(promise);
    });
    await Promise.all(promises);
  }
  /**
   * 移除表单项的校验结果提示
   * @public
   */
  clearValidate() {
    // 此处只关系当前表单直接子元素，因为接下来会递归调用 子 form 的 clearValidate
    this.editors.forEach(editor => editor.clearValidate());
    this.forEachSubForms(subForm => subForm.clearValidate());
  }
  /**
   * 校验表单，如果表单有错误，会以数组的方式返回错误列表
   * @public
   */
  async validate() {
    if (validatingForms.has(this)) {
      console.warn('[UICore] 校验出现循环递归，请检查是否有组件在“校验时”行为内，又错误地配置了一个“校验表单”');
      return [];
    }
    validatingForms.add(this);
    const result = [];
    await Promise.all([...Array.from(this.editors, async editor => {
      editor.clearValidate();
      result.push(...(await editor.validateAndUpdateStatus()));
    }), ...Array.from(this.subForms, async subForm => {
      // subForm.clearValidate(); // TODO: 如果表单也要展示状态，就需要补上这行
      result.push(...(await subForm.validate()));
    })]);
    validatingForms.delete(this);
    const mixErrors = err => {
      if (!err) return;
      const items = arrayify(err).filter(x => x && !x.isFlowAbortError) // 排除掉流程终止导致的错误
      .map(x => normalizeFormValidateResult(this, x));
      if (!items.length) return;
      result.push(...items);
    };
    if (!result.length && typeof this.onValidate === 'function') {
      // 子表单 & 子输入框没错误，需要走当前Form的补充校验逻辑
      await Promise.resolve().then(() => {
        this.onValidate();
      }).catch(err => err).then(mixErrors);
    }
    // let hasCustomFailHandler = false;
    if (result.length && typeof this.onValidateFailed === 'function') {
      // 校验出错，需要调用 onValidateFailed 回调
      // 此时可能会对 result 数组做一些骚操作
      // hasCustomFailHandler = true;
      await Promise.resolve().then(() => {
        this.onValidateFailed(result);
      }).catch(err => err).then(mixErrors);
    }
    // if (!hasCustomFailHandler && result.length && validatingForms.size === 0) {
    //   // 最后，如果有错误信息，而且已经是最外层Form，就显示提示
    //   await this.uc?.$alert(`表单校验失败：\n\n${result.map(x => ` - ${x.message}`).join('\n')}`);
    //   const editor = result[0]?.editor;
    //   if (editor) {
    //     focusEditor(editor);
    //     highlightRect(toRect(editor));
    //   }
    // }
    return result;
  }
  async validateAndThrow() {
    const result = await this.validate();
    if (result.length) throw new Error(`请按要求填写完整信息 \n\n${result.map(x => x.message).join('\n')}`);
  }
  // -------------------------------------------
  created() {
    this.isDesignMode = !!this.$attrs['data-uc-design-mode'];
    this.validateDebounced = debounce(() => this.validate(), 500);
    this.$watch(() => {
      var _this$$vnode;
      return ((_this$$vnode = this.$vnode) === null || _this$$vnode === void 0 || (_this$$vnode = _this$$vnode.data) === null || _this$$vnode === void 0 ? void 0 : _this$$vnode.ref) || '';
    }, val => {
      this.formComponentId = val;
    }, {
      immediate: true
    });
  }
  mounted() {
    var _this$parentForm6;
    if (!this.isRoot) (_this$parentForm6 = this.parentForm) === null || _this$parentForm6 === void 0 || _this$parentForm6.subForms.add(this);
  }
  beforeDestroy() {
    var _this$parentForm7;
    this.validateDebounced.cancel();
    if (!this.isRoot) (_this$parentForm7 = this.parentForm) === null || _this$parentForm7 === void 0 || _this$parentForm7.subForms.delete(this);
  }
};
FormContainerBase = __decorate([Component({
  name: 'FormContainerBase',
  props: {
    onValidate: {
      type: Function,
      required: false
    },
    onValidateFailed: {
      type: Function,
      required: false
    }
  },
  ucMethods(decls) {
    decls.push({
      id: 'reset',
      title: '重置为默认值',
      description: '将重置此区域内的输入框组件'
    }, {
      id: 'validate',
      title: '校验输入'
    }, {
      id: 'validateAndThrow',
      title: '校验输入（出错就阻断流程）'
    }, {
      id: 'clearValidate',
      title: '清除校验结果提示'
    });
  },
  ucEvents(decls) {
    decls.push({
      id: ':onValidate',
      name: '校验通过',
      desc: '该区域内的输入框 **都校验通过** 时触发\n\n可补充更多逻辑，通过 JavaScript 代码 `throw new Error("自定义错误信息")` 抛出错误。如果这里抛出错误，会触发“校验不通过”的事件'
    }, {
      id: ':onValidateFailed',
      name: '校验不通过',
      desc: '',
      arguments: [{
        id: 'errors',
        type: 'array',
        title: '错误列表',
        items: {
          type: 'object',
          fields: [{
            id: 'message',
            type: 'string',
            title: '错误描述'
          }, {
            id: 'editor',
            type: 'any',
            title: '对应组件实例(可能为空)'
          }]
        }
      }]
    });
  }
})], FormContainerBase);
export default FormContainerBase;
//# sourceMappingURL=FormContainerBase.js.map
